import 'react-image-crop/dist/ReactCrop.css';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import {
  ChangeEvent, useRef, useState,
} from 'react';
import { useDebounceEffect } from 'src/lib/useDebounceEffect';
import { canvasPreview } from 'src/lib/canvasPreview';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import { ArrowBack } from '@material-ui/icons';
import SaveIcon from '@material-ui/icons/Save';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Resizer from 'react-image-file-resizer';
import { t } from 'src/lib/language';
import { MODALS } from 'src/models/ModalModel';
import { uploadFileAction } from 'src/redux/actions/uploadFileAction';
import { closeModal } from 'src/redux/actions/modalActions';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { FORM, FORM_STATUS } from 'src/constants/Form';

const useStyles = makeStyles(() => ({
  row: {
    width: '100%',
    padding: '8px 0px',
    display: 'flex',
    justifyContent: 'left',
    gap: '8px',
    alignItems: 'center',
  },
  header: {
    fontSize: '1.2rem',
    fontFamily: 'Qanelas-Bold',
    marginTop: '16px',
  },
  scroller: {
    overflowY: 'auto',
    height: '72vh',
    padding: '8px',
  },
}));

interface ImageSize {
  width: number;
  height: number;
  ratio: number;
}

interface Payload {
  size: ImageSize;
  path: string;
  name: string;
  maxSize?: number;
  onSave?: (file: string, name?: string) => void;
  onUpload?: (path: string) => void;
  conservePath?: boolean;
}

const FormImageCropper = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const formStatus = useSelector(
    (state: Store) => state.formStatus[FORM.upload_image] ?? null,
  );

  const {
    size, path, name, onSave, onUpload, conservePath = false,
  } = useSelector(
    (state: Store) => state.modals[MODALS.uploadImage]?.payload as Payload,
  );

  const InitialCrop: PixelCrop = {
    unit: 'px',
    x: 25,
    y: 25,
    width: size.width,
    height: size.height,
  };
  const lang = useSelector((state: Store) => state.language.language);
  const [crop, setCrop] = useState<Crop>(InitialCrop);
  const [imgSrc, setImgSrc] = useState('');
  const [image, setImage] = useState({} as File);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const onSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.onload = () => {
        if (!!reader.result?.toString()) {
          const newImage = new Image();
          newImage.onload = () => {
            if ((newImage.width < size.width) || (newImage.height < size.height)) {
              dispatch(setAlert({
                type: ALERT_TYPE.error,
                message: `Image too small, minimum size is ${size.width}px X ${size.height}px`,
              }));
              return;
            }
            setCrop(InitialCrop); // Makes crop preview update between images.
            // @ts-ignore
            setImgSrc(reader.result?.toString());
            // @ts-ignore
            setImage(e.target.files[0]);
          };
          newImage.src = reader.result?.toString();
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const getNormalizedCrop = (pixelCrop: PixelCrop) => {
    if (imgRef.current) {
      const ratio = imgRef.current?.width / imgRef.current?.naturalWidth;
      const { width, height } = pixelCrop;
      return {
        ...pixelCrop,
        width: width * ratio,
        height: height * ratio,
      } as PixelCrop;
    }
    return InitialCrop;
  };

  const onImageLoad = () => {
    setCrop(getNormalizedCrop(InitialCrop));
    setCompletedCrop(getNormalizedCrop(InitialCrop));
  };

  const handleSettingImage = () => {};

  const resizeAndUpload = (result: any) => {
    const commaPosition = result.indexOf(',') + 1;
    const file = result.substring(commaPosition) as string;

    if (onSave) {
      onSave(file, image.name);
      dispatch(closeModal({ modal: MODALS.uploadImage }));
      return;
    }

    // Get extension from original image
    const originalExt = image.name.split('.').pop() || '';
    const ext = originalExt ? `.${originalExt}` : '';

    let filename;
    if (name) {
      filename = `${name}${ext}`; // Use payload name + extension
    } else {
      filename = image.name; // Use original image.name
    }

    const fullPath = conservePath ? path : `${path}/${filename}`;
    const mimeType = result.match(/data:([^;]+);base64,/)?.[1] || 'image/jpeg';

    dispatch(uploadFileAction(
      file,
      fullPath,
      mimeType,
      t(lang, 'messages.successfully_uploaded'),
      t(lang, 'messages.error_upload'),
      onUpload,
    ));
    handleSettingImage();
  };

  const onBack = () => {
    dispatch(closeModal({ modal: MODALS.uploadImage }));
  };

  const onSaveFile = () => {
    if (previewCanvasRef.current) {
      previewCanvasRef.current?.toBlob((blob:Nullable<Blob>) => {
        if (blob) {
          const file = new File([blob], name || image?.name);
          Resizer.imageFileResizer(
            file,
            size.width,
            size.height,
            'JPEG', // ← PNG → JPEG
            85, // ← 100 → 85
            0,
            resizeAndUpload,
            'base64',
          );
        }
      });
    }
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        await canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          1,
          0,
        );
      }
    },
    100,
    [completedCrop, crop],
  );
  return (
    <div className="App">
      <div className={classes.header}>Cropping</div>
      <div className={classes.scroller}>
        <div className={classes.row}>
          <Button variant="contained" color="primary" onClick={onBack}>
            <ArrowBack />Back
          </Button>
          <Button variant="contained" component="label" color="primary">
            <AddIcon /> Choose a file
            <input type="file" hidden accept="image/*" onChange={onSelectFile} />
          </Button>
          <span style={{ padding: '8px' }}>{image.name}</span>
        </div>
        { Boolean(imgSrc) && (
          <ReactCrop
            crop={crop}
            minHeight={getNormalizedCrop(InitialCrop).height}
            minWidth={getNormalizedCrop(InitialCrop).width}
            onChange={(pixelCrop) => setCrop(pixelCrop)}
            onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
            aspect={size.ratio}>
            <img
              ref={imgRef}
              alt="Crop me"
              src={imgSrc}
              onLoad={() => onImageLoad()} />
          </ReactCrop>
        ) }
        <div>
          { Boolean(completedCrop) && (
            <>
              <div className={classes.header}>Preview</div>
              <canvas
                ref={previewCanvasRef}
                width={size.width / 4}
                height={size.height / 4}
                style={{
                  border: '1px solid black',
                  objectFit: 'contain',
                  maxWidth: 800, // Max browser width = 800px
                  width: '100%', // Responsive up to the max
                  height: 'auto', // Maintain aspect ratio
                  marginTop: '8px',
                }} />
              <div className={classes.row}>
                <Button variant="contained" color="primary" onClick={onBack}>
                  <ArrowBack />Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onSaveFile}
                  disabled={formStatus === FORM_STATUS.processing}>
                  <SaveIcon />Save
                </Button>
              </div>
            </>
          ) }
        </div>
      </div>
    </div>
  );
};

export default FormImageCropper;
